import { useCallback, useMemo } from "react"
import { useQuery } from "@rocicorp/zero/react"

import type {
	MessageReactionType,
	ReactionGroup,
} from "@productlane/lib/src/message-reactions"
import { groupReactionsByMessage } from "@productlane/lib/src/message-reactions"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import { toast } from "@/components/toast"
import { generateId } from "@/lib/id"
import { mutateOk, useAuthData, useZero } from "@/lib/zero"

const EMPTY: ReactionGroup[] = []

/** Every message's reaction chips for the open thread. One shared query per thread. */
export function useThreadReactionGroups(threadId: string) {
	const { userId } = useAuthData()
	const [rows] = useQuery(queries.threadMessageReactions(threadId), {
		ttl: "10m",
	})
	return useMemo(
		() => groupReactionsByMessage(rows ?? [], userId),
		[rows, userId],
	)
}

export function useMessageReactions(threadId: string, messageId: string) {
	const groups = useThreadReactionGroups(threadId)
	return groups.get(messageId) ?? EMPTY
}

export const REACTION_COPY = {
	add: "Add reaction",
	failed: "Reaction not saved",
	viewer: "Viewers can't react to messages",
	automatic: "Productlane",
	someone: "Someone",
}

export function useToggleReaction(
	threadId: string,
	messageType: MessageReactionType,
	messageId: string,
) {
	const zero = useZero()
	return useCallback(
		async (emoji: { name: string; imageUrl: string | null }) => {
			try {
				await mutateOk(
					zero.mutate(
						mutators.messageReactions.toggle({
							id: generateId(),
							threadId,
							messageType,
							messageId,
							emoji: emoji.name,
							imageUrl: emoji.imageUrl,
						}),
					),
					"server",
				)
			} catch (error) {
				toast.error({
					title: REACTION_COPY.failed,
					description: error instanceof Error ? error.message : undefined,
				})
			}
		},
		[zero, threadId, messageType, messageId],
	)
}
